import { queryClient } from '@qse/api'
import { AppRoutesList } from '@qse/constants'
import { destroyGlobalWebSocketManager } from '@qse/realtime'
import {
  clearAllStores,
  useBillingStatusStore,
  useProgressTrackerStore,
  useUserStore,
  useWebSocketStore,
} from '@qse/stores'
import { handleLogoutUploadAbort, resetGlobalFileDownload, resetGlobalFileUpload } from '@qse/ui'
import { useNavigate } from 'react-router-dom'

import { authService } from './auth-service'
import { broadcastLogout } from './use-cross-tab-logout'

export const useLogout = () => {
  const navigate = useNavigate()

  const clearClientSideData = async (skipModalStore = false) => {
    // Clear all stores, cancel all ongoing requests, and clear storage

    // 1. Gracefully abort all ongoing uploads BEFORE clearing state
    // This ensures uploads are cancelled with proper error messages and backend cleanup
    try {
      await handleLogoutUploadAbort(queryClient)
    } catch (error) {
      console.warn('Could not gracefully abort uploads:', error)
      // Continue with logout even if abort fails
    }

    // 2. Disconnect all WebSocket connections
    // This must happen before clearing storage to ensure proper cleanup
    try {
      destroyGlobalWebSocketManager()

      // Add a small delay to ensure WebSocket cleanup is complete
      await new Promise(resolve => setTimeout(resolve, 100))
    } catch (error) {
      console.warn('Could not disconnect WebSocket connections:', error)
    }

    // 3. Cancel and clear all React Query operations
    // This prevents any pending API calls from completing after logout
    queryClient.cancelQueries()
    queryClient.clear()

    // 4. Clear browser storage
    // This removes auth tokens and other sensitive data
    localStorage.clear()
    sessionStorage.clear()

    // 5. Clear all stores (including WebSocket store)
    // This resets all application state to initial values
    // If skipModalStore is true, we keep the modal open during logout
    if (skipModalStore) {
      // Clear stores individually to avoid clearing modal store
      // This keeps the drive setup modal visible during logout
      useUserStore.getState()?.reset?.()
      useBillingStatusStore.getState()?.reset?.()
      useProgressTrackerStore.getState()?.reset?.()
      useWebSocketStore.getState()?.reset?.()
    } else {
      clearAllStores()
    }

    // 6. Clear file upload and download stores
    // This resets file transfer progress and states
    try {
      resetGlobalFileUpload()
      resetGlobalFileDownload()
    } catch (error) {
      console.warn('Could not reset file stores:', error)
    }
  }

  const redirectToLogin = (() => {
    let hasRedirected = false

    return () => {
      if (hasRedirected) {
        return
      }

      hasRedirected = true
      navigate(AppRoutesList.login, { replace: true })
    }
  })()

  const handleLogout = async (options?: {
    onSuccess?: () => void
    onError?: (errorMessage: string) => void
    skipModalStore?: boolean
  }) => {
    try {
      // Broadcast logout to other tabs FIRST
      broadcastLogout()

      await authService.logout()
      redirectToLogin()

      // Clear user data and set authentication to false
      // Keep modal open if skipModalStore is true (useful for drive setup modal during logout)
      await clearClientSideData(options?.skipModalStore)
      options?.onSuccess?.()
    } catch (error: any) {
      const errorMessage = authService.getUserFriendlyError(error, 'logout')

      // Broadcast logout even on error
      broadcastLogout()
      redirectToLogin()

      // Clear user data and set authentication to false even on error
      await clearClientSideData(options?.skipModalStore)

      options?.onError?.(errorMessage)
    }
  }

  // Force logout without calling AWS - useful for expired sessions
  const forceLogout = async () => {
    try {
      // Broadcast logout to other tabs
      broadcastLogout()

      authService.forceLogout()

      // Clear user data and set authentication to false
      await clearClientSideData()

      setTimeout(() => {
        window.location.href = AppRoutesList.login
      }, 500)
    } catch (error: any) {
      console.error('Force logout error:', error)

      // Broadcast logout even on error
      broadcastLogout()

      // Clear user data and set authentication to false even on error
      await clearClientSideData()

      // Fallback to window location change
      window.location.href = AppRoutesList.login
    }
  }

  return {
    handleLogout,
    forceLogout,
  }
}
